"use client";

import { useCallback, useEffect, useRef } from "react";
import {
  useInfiniteQuery,
  useQuery,
  useQueryClient,
  type QueryClient,
} from "@tanstack/react-query";
import type { components, Page } from "@theta/api-client";
import { thetaApi, dataOf, pollablePageOf } from "@/lib/api/theta";
import { meKey, useMe } from "./useConversations";
import { retryUnlessRefused } from "./queryRetry";

type Schemas = components["schemas"];

/**
 * NOTIFICATIONS, ON /api/v1 — AND 170 LINES OF FORMATTER DELETED
 *
 * THE TEXT IS SERVER-RENDERED NOW (§4.1 step 3, §1.12). Every row carries
 * `text` (Danish), `href` and `template: {key, params}`. What that replaces,
 * in this file alone:
 *
 *  - `formatNotifText`, an 18-arm switch over integer notification codes that
 *    rebuilt every sentence on the client;
 *  - `formatActorNames`, which reimplemented Danish list grammar — "A og B",
 *    "A, B og C", "A, B og 3 andre" — including the `anden`/`andre` singular
 *    split, from `unique_actor_count` and a partial `other_actor_names` array;
 *  - `getNotifHref`, a ten-branch cascade over `topic_id`, `post_number`,
 *    `group_slug`, `conversation_id` and `event_id` that guessed the URL the
 *    server already knew;
 *  - `lib/notificationTypes.ts` and its drift test, whose own header said
 *    "delete this file, this test and that import together" once this hook
 *    stopped switching on the codes. It has.
 *
 * `notificationType` is a STRING open enum now (D15), so a client renders a
 * type it has never heard of — which is exactly what the old integer switch's
 * `default:` arm could not do, because it had no text to fall back to.
 *
 * WHAT ELSE CHANGED
 *  - CURSOR PAGES (§1.3). The legacy endpoint capped at 60 rows and the page
 *    silently ended there; this one keeps going.
 *  - `?ifChanged=` instead of a 5-second refetch of the whole list (D12), and
 *    the poll is bounded to the FIRST page — somebody who has scrolled into
 *    history is reading, not watching.
 *  - THE UNREAD COUNT IS THE SERVER'S. It comes from `GET /me`'s badge
 *    counters, not from `notifications.filter(n => !n.read).length` over
 *    whatever the page happened to hold. It counts ROWS of this list — see
 *    `useUnreadNotificationCount`.
 */
export type Notification = Schemas["Notification"];
type MarkNotificationsReadResult = Schemas["MarkNotificationsReadResult"];
type MarkNotificationsUnreadResult = Schemas["MarkNotificationsUnreadResult"];

export const notificationsKey = ["v1", "notifications"] as const;

/** The cadence to use until the server has named one (D12). */
const FALLBACK_POLL_MS = 5_000;

const EMPTY_PAGE: Page<Notification> = {
  items: [],
  nextCursor: null,
  prevCursor: null,
  hasMore: false,
};

/**
 * `enabled` IS ABOUT BEING SIGNED IN, and it is not optional politeness
 * (OSO-575). `GET /notifications` is the one header read with no anonymous
 * answer — `GET /me` returns `null` for a signed-out visitor, this route
 * returns 401 — so an ungated hook had every anonymous tab asking, every five
 * seconds, for a document that cannot exist, for as long as it stayed open.
 * Found in the breadcrumbs of a Sentry event from a shared link opened in
 * Facebook's in-app browser: five 401s in half a minute, on a page with no
 * bell on it.
 *
 * The same shape as `useConversations`, second gate included: a disabled query
 * does not poll either, and saying so in `refetchInterval` too costs one `&&`
 * and removes the question.
 */
export function useNotifications({ enabled = true }: { enabled?: boolean } = {}) {
  const seen = useRef(new Map<string, { state: string; page: Page<Notification> }>());
  const pollAfterMs = useRef<number | null>(null);

  return useInfiniteQuery({
    queryKey: notificationsKey,
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) => {
      const key = pageParam ?? "";
      const previous = seen.current.get(key);

      const result = pollablePageOf<Notification>(
        await thetaApi.http.GET("/notifications", {
          params: {
            query: {
              ...(pageParam ? { cursor: pageParam } : {}),
              ...(previous ? { ifChanged: previous.state } : {}),
            },
          },
        }),
      );

      pollAfterMs.current = result.pollAfterMs;
      // An unchanged tick has NO document — returning the same object
      // reference is what stops react-query re-rendering the bell every tick.
      if (result.unchanged) return previous?.page ?? EMPTY_PAGE;

      seen.current.set(key, { state: result.state ?? "", page: result.data });
      return result.data;
    },
    getNextPageParam: (last) => (last.hasMore ? last.nextCursor : undefined),
    enabled,

    refetchInterval: (query) =>
      enabled &&
      typeof document !== "undefined" &&
      document.visibilityState !== "hidden" &&
      (query.state.data?.pages.length ?? 0) <= 1
        ? (pollAfterMs.current ?? FALLBACK_POLL_MS)
        : (false as const),
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    staleTime: 0,
    gcTime: 5 * 60 * 1000,
  });
}

/** Every loaded page, flattened. The list screens want one array. */
export function notificationsOf(
  data: { pages: Page<Notification>[] } | undefined,
): Notification[] {
  return data ? data.pages.flatMap((page) => page.items) : [];
}

type NotificationPages = { pages: Page<Notification>[]; pageParams: unknown[] };

/** The branded id — `EntityId<"notification">`, so a post id cannot be passed here. */
type NotificationId = Notification["id"];

/**
 * Set the unread count of ONE collapsed row, keeping `isRead` in step with it.
 * The optimistic write, its reconciliation and its rollback all go through
 * here, in both directions.
 *
 * OSO-524 × OSO-519: `unreadCount` is what `UnreadMarker` reads, so it must
 * move with `isRead` — an unread row with a count of 0 shows no marker at all,
 * and a read row with a count shows one nobody can clear. The marker is a dot
 * either way (OSO-586); the count decides whether it is drawn, not what it says.
 *
 * OSO-536: A COUNT, NOT A FLAG. `groupedIds` is capped
 * (`NOTIFICATION_GROUP_MEMBER_LIMIT`) at exactly what `POST /notifications/read`
 * accepts, so a row collapsing MORE notifications than that is only PARTLY
 * covered by the one request its ids can make — the rest were clipped before
 * the client ever saw them. Forcing the count to 0 claimed the row was cleared
 * when the server had marked what it was handed and no more; setting it from
 * what the server reports leaves the mark on the row, where it agrees with the
 * bell badge — which still counts that row, because a row with anything unread
 * left in it counts one. Such a row is emptied by "marker alle som læst".
 */
function setUnreadCount(
  data: NotificationPages | undefined,
  id: NotificationId,
  unread: number,
): NotificationPages | undefined {
  if (!data) return data;
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      items: page.items.map((n) => {
        if (n.id !== id) return n;
        const unreadCount = Math.min(Math.max(unread, 0), n.notificationCount);
        return { ...n, unreadCount, isRead: unreadCount === 0 };
      }),
    })),
  };
}

/** The row's unread count as the cache holds it now — what every write moves from. */
function unreadCountOf(queryClient: QueryClient, id: NotificationId): number {
  const rows = notificationsOf(
    queryClient.getQueryData<NotificationPages>(notificationsKey),
  );
  return rows.find((n) => n.id === id)?.unreadCount ?? 0;
}

/**
 * §1.8's ONE bulk operation: `POST /notifications/read` with `{ids}` or
 * `{all: true}`, never both. It replaces N sequential `PUT
 * /notifications/mark-read` calls — one per id in a collapsed group.
 *
 * THE RESPONSE IS PER-ITEM RESULTS, NOT A STATUS. An unknown, foreign or
 * already-read id comes back as `not_found` / `already_read` rather than
 * failing the request, because collapsed batches overlap between polls and a
 * 404 would turn the normal case into a failed mutation that rolled the bell
 * back. So the optimistic update RECONCILES against `results` — the row's count
 * is set from how many ids the server really marked — instead of assuming the
 * whole batch took.
 */
export function useMarkNotificationRead() {
  const queryClient = useQueryClient();

  return useCallback(
    async (id: NotificationId, groupedIds?: NotificationId[]) => {
      const ids = groupedIds && groupedIds.length > 1 ? groupedIds : [id];
      const before = unreadCountOf(queryClient, id);

      // The optimistic guess assumes every id sent is unread. `results` then
      // says how many really were: `already_read` and `not_found` both mean
      // this request cleared nothing, so the row keeps that member.
      queryClient.setQueryData<NotificationPages>(notificationsKey, (old) =>
        setUnreadCount(old, id, before - ids.length),
      );

      try {
        const result = dataOf<MarkNotificationsReadResult>(
          await thetaApi.http.POST("/notifications/read", { body: { ids } }),
        );
        const marked = result.results.filter((r) => r.status === "marked").length;
        queryClient.setQueryData<NotificationPages>(notificationsKey, (old) =>
          setUnreadCount(old, id, before - marked),
        );
      } catch {
        queryClient.setQueryData<NotificationPages>(notificationsKey, (old) =>
          setUnreadCount(old, id, before),
        );
      }
      // The badge lives on `GET /me`, so it is what has to be re-read.
      queryClient.invalidateQueries({ queryKey: meKey });
    },
    [queryClient],
  );
}

/**
 * OSO-519, and deliberately the same hook body upside down: the same
 * `setUnreadCount` helper, the same reconcile-against-`results` step, the same
 * badge invalidation. A bell row is read by being looked at, so this is the
 * only way back — "gem den til senere".
 *
 * ALWAYS THE WHOLE COLLAPSED GROUP, never the single primary id. Marking read
 * takes `groupedIds` and so does this; taking only `id` would put one member of
 * ten back and leave the row saying it holds nine things the member has dealt
 * with and one they have not.
 *
 * There is no `useMarkAllNotificationsUnread` and there is no endpoint for one.
 */
export function useMarkNotificationUnread() {
  const queryClient = useQueryClient();

  return useCallback(
    async (id: NotificationId, groupedIds?: NotificationId[]) => {
      const ids = groupedIds && groupedIds.length > 1 ? groupedIds : [id];
      const before = unreadCountOf(queryClient, id);

      queryClient.setQueryData<NotificationPages>(notificationsKey, (old) =>
        setUnreadCount(old, id, before + ids.length),
      );

      try {
        const result = dataOf<MarkNotificationsUnreadResult>(
          await thetaApi.http.POST("/notifications/unread", { body: { ids } }),
        );
        const marked = result.results.filter((r) => r.status === "marked").length;
        queryClient.setQueryData<NotificationPages>(notificationsKey, (old) =>
          setUnreadCount(old, id, before + marked),
        );
      } catch {
        queryClient.setQueryData<NotificationPages>(notificationsKey, (old) =>
          setUnreadCount(old, id, before),
        );
      }
      queryClient.invalidateQueries({ queryKey: meKey });
    },
    [queryClient],
  );
}

/**
 * `{all: true}` is BELL-SCOPED — the same set `GET /notifications` lists and
 * the same set `GET /me`'s badge counts. It does not touch the DM inbox.
 */
export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();

  return useCallback(async () => {
    const previous = queryClient.getQueryData<NotificationPages>(notificationsKey);
    queryClient.setQueryData<NotificationPages>(notificationsKey, (old) =>
      old
        ? {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              items: page.items.map((n) => ({ ...n, isRead: true, unreadCount: 0 })),
            })),
          }
        : old,
    );

    try {
      dataOf<MarkNotificationsReadResult>(
        await thetaApi.http.POST("/notifications/read", { body: { all: true } }),
      );
    } catch {
      queryClient.setQueryData(notificationsKey, previous);
    }
    queryClient.invalidateQueries({ queryKey: meKey });
  }, [queryClient]);
}

/**
 * The bell badge. The SERVER's number, from `GET /me` — not a count of the
 * unread rows in whatever page happens to be loaded, which could not see past
 * its own end.
 *
 * IT COUNTS ROWS OF THE LIST (OSO-586), collapsed the way the list collapses
 * them: three comments on one post are one row and one point of this number.
 * So the badge is countable from the screen — one dot, one point — which is
 * the property it exists for and the one it did not have while it counted raw
 * notifications.
 */
export function useUnreadNotificationCount(): number {
  const { data: me } = useMe();
  const notifications = me?.badges.unreadNotificationCount ?? 0;

  /**
   * The OS badge covers everything unread — notifications AND conversations.
   * This is deliberately the app's ONLY `setAppBadge` caller; a second one
   * would fight over the badge. Both numbers come from the SAME `/me`
   * document (§1.10), so they can no longer disagree, and the RETURN value
   * stays notifications-only so the bell badge is unchanged.
   */
  const total = notifications + (me?.badges.unreadConversationCount ?? 0);

  useEffect(() => {
    if (typeof navigator === "undefined" || !("setAppBadge" in navigator)) return;
    if (total > 0) {
      navigator.setAppBadge(total);
    } else {
      navigator.clearAppBadge();
    }
  }, [total]);

  return notifications;
}

/**
 * ONE notification, read by its own id (OSO-666).
 *
 * `/afgoerelser/{id}` is reached from the bell, from a push tap and from an
 * e-mail, so it cannot assume the list is in the cache — and the row it needs
 * may be pages down. `GET /notifications/{id}` answers 404 for anybody else's,
 * which is what makes a guessed id useless.
 *
 * NOT POLLED. A moderation decision does not change after it is taken; the
 * only thing that could is the group's name, and re-reading for that is not
 * worth a request a second on a page somebody is reading.
 */
export function useNotification(id: number | null) {
  return useQuery({
    queryKey: ["v1", "notification", id],
    enabled: id !== null,
    queryFn: () =>
      thetaApi.http
        .GET("/notifications/{id}", { params: { path: { id: id! } } })
        .then(dataOf<Notification>),
    retry: retryUnlessRefused,
    staleTime: 5 * 60 * 1000,
  });
}
